<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { XMarkIcon, IdentificationIcon, CloudArrowUpIcon, DocumentTextIcon, CheckCircleIcon } from '@heroicons/vue/24/outline';

interface Props {
  isOpen: boolean;
  isUploading?: boolean;
}

interface Emits {
  (e: 'close'): void;
  (e: 'upload', selfieFile: File, idFile: File): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Selfie with ID
const selfieFile = ref<File | null>(null);
const selfiePreviewUrl = ref<string | null>(null);
const selfieError = ref<string | null>(null);
const selfieDragActive = ref(false);

// School ID only
const idFile = ref<File | null>(null);
const idPreviewUrl = ref<string | null>(null);
const idError = ref<string | null>(null);
const idDragActive = ref(false);

const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
const MAX_SIZE_MB = 5;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

const canUpload = computed(() => {
  return selfieFile.value !== null && idFile.value !== null && !props.isUploading;
});

watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    resetState();
  }
});

const resetState = () => {
  selfieFile.value = null;
  selfiePreviewUrl.value = null;
  selfieError.value = null;
  selfieDragActive.value = false;
  
  idFile.value = null;
  idPreviewUrl.value = null;
  idError.value = null;
  idDragActive.value = false;
};

const validateFile = (file: File): string | null => {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return 'Please upload a valid image (JPG, PNG) or PDF file.';
  }
  
  if (file.size > MAX_SIZE_BYTES) {
    return `File size must be less than ${MAX_SIZE_MB}MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`;
  }
  
  return null;
};

// Selfie with ID handlers
const handleSelfieFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  
  if (file) {
    processSelfieFile(file);
  }
};

const processSelfieFile = (file: File) => {
  const error = validateFile(file);
  
  if (error) {
    selfieError.value = error;
    selfieFile.value = null;
    selfiePreviewUrl.value = null;
    return;
  }
  
  selfieFile.value = file;
  selfieError.value = null;
  
  // Create preview if it's an image
  if (file.type.startsWith('image/')) {
    const reader = new FileReader();
    reader.onload = (e) => {
      selfiePreviewUrl.value = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  } else {
    selfiePreviewUrl.value = null;
  }
};

const handleSelfieDragOver = (event: DragEvent) => {
  event.preventDefault();
  selfieDragActive.value = true;
};

const handleSelfieDragLeave = () => {
  selfieDragActive.value = false;
};

const handleSelfieDrop = (event: DragEvent) => {
  event.preventDefault();
  selfieDragActive.value = false;
  
  const file = event.dataTransfer?.files[0];
  if (file) {
    processSelfieFile(file);
  }
};

const removeSelfieFile = () => {
  selfieFile.value = null;
  selfiePreviewUrl.value = null;
  selfieError.value = null;
};

// School ID only handlers
const handleIDFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  
  if (file) {
    processIDFile(file);
  }
};

const processIDFile = (file: File) => {
  const error = validateFile(file);
  
  if (error) {
    idError.value = error;
    idFile.value = null;
    idPreviewUrl.value = null;
    return;
  }
  
  idFile.value = file;
  idError.value = null;
  
  // Create preview if it's an image
  if (file.type.startsWith('image/')) {
    const reader = new FileReader();
    reader.onload = (e) => {
      idPreviewUrl.value = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  } else {
    idPreviewUrl.value = null;
  }
};

const handleIDDragOver = (event: DragEvent) => {
  event.preventDefault();
  idDragActive.value = true;
};

const handleIDDragLeave = () => {
  idDragActive.value = false;
};

const handleIDDrop = (event: DragEvent) => {
  event.preventDefault();
  idDragActive.value = false;
  
  const file = event.dataTransfer?.files[0];
  if (file) {
    processIDFile(file);
  }
};

const removeIDFile = () => {
  idFile.value = null;
  idPreviewUrl.value = null;
  idError.value = null;
};

const handleUpload = () => {
  if (selfieFile.value && idFile.value && !props.isUploading) {
    emit('upload', selfieFile.value, idFile.value);
  }
};

const handleClose = () => {
  if (!props.isUploading) {
    emit('close');
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
};
</script>

<template>
  <Transition name="modal">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      @click.self="handleClose"
    >
      <div
        class="bg-white rounded-2xl shadow-[8px_8px_0_0_rgba(0,0,0,1)] border-2 border-black max-w-lg w-full max-h-[90vh] flex flex-col transform transition-all overflow-hidden"
        @click.stop
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-5 pt-4 pb-3 bg-white  flex-shrink-0">
          <div class="flex items-center gap-2">
            <div class="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <IdentificationIcon class="w-6 h-6 text-blue-600" />
            </div>
            <h2 class="text-xl font-bold text-gray-900">Upload School ID</h2>
          </div>
          <button
            @click="handleClose"
            :disabled="isUploading"
            class="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Close"
          >
            <XMarkIcon class="w-5 h-5" />
          </button>
        </div>

        <!-- Content (Scrollable) -->
        <div class="p-4 space-y-4 overflow-y-auto flex-1">
          <!-- Document 1: Selfie with School ID -->
          <div>
            <div class="flex items-center gap-2 mb-2">
              <span class="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                1
              </span>
              <h3 class="text-sm font-bold text-gray-900">Selfie with School ID</h3>
              <CheckCircleIcon v-if="selfieFile" class="w-5 h-5 text-green-600 ml-auto" />
            </div>
            
            <!-- Upload Area or Preview for Selfie -->
            <div v-if="!selfieFile">
              <div
                @dragover="handleSelfieDragOver"
                @dragleave="handleSelfieDragLeave"
                @drop="handleSelfieDrop"
                :class="[
                  'border-2 border-dashed rounded-lg p-4 text-center transition-all cursor-pointer',
                  selfieDragActive 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/30'
                ]"
              >
                <input
                  type="file"
                  id="selfie-file-input"
                  class="hidden"
                  accept="image/jpeg,image/jpg,image/png"
                  @change="handleSelfieFileSelect"
                />
                
                <label for="selfie-file-input" class="cursor-pointer">
                  <CloudArrowUpIcon class="w-10 h-10 mx-auto text-blue-500 mb-1.5" />
                  <p class="text-xs font-semibold text-gray-700 mb-1">
                    Upload selfie holding your ID
                  </p>
                  <p class="text-xs text-gray-500 mb-2">
                    JPG, PNG (Max {{ MAX_SIZE_MB }}MB)
                  </p>
                  <div class="inline-block px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors font-medium">
                    Choose File
                  </div>
                </label>
              </div>

              <!-- Error Message for Selfie -->
              <div v-if="selfieError" class="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <svg class="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
                <p class="text-xs text-red-700">{{ selfieError }}</p>
              </div>
            </div>

            <!-- Selfie Preview -->
            <div v-else class="relative">
              <div class="border-2 border-green-300 rounded-lg overflow-hidden">
                <img
                  v-if="selfiePreviewUrl"
                  :src="selfiePreviewUrl"
                  alt="Selfie Preview"
                  class="w-full h-auto max-h-40 object-contain bg-gray-50"
                />
              </div>
              <button
                @click="removeSelfieFile"
                :disabled="isUploading"
                class="absolute top-1.5 right-1.5 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg border-2 border-white disabled:opacity-50 disabled:cursor-not-allowed"
                title="Remove file"
              >
                <XMarkIcon class="w-3.5 h-3.5" />
              </button>
              <p class="text-xs text-gray-600 mt-1.5 truncate">📷 {{ selfieFile.name }} ({{ formatFileSize(selfieFile.size) }})</p>
            </div>
          </div>

          <!-- Document 2: School ID Only -->
          <div>
            <div class="flex items-center gap-2 mb-2">
              <span class="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                2
              </span>
              <h3 class="text-sm font-bold text-gray-900">School ID Only</h3>
              <CheckCircleIcon v-if="idFile" class="w-5 h-5 text-green-600 ml-auto" />
            </div>
            
            <!-- Upload Area or Preview for ID -->
            <div v-if="!idFile">
              <div
                @dragover="handleIDDragOver"
                @dragleave="handleIDDragLeave"
                @drop="handleIDDrop"
                :class="[
                  'border-2 border-dashed rounded-lg p-4 text-center transition-all cursor-pointer',
                  idDragActive 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/30'
                ]"
              >
                <input
                  type="file"
                  id="id-file-input"
                  class="hidden"
                  accept="image/jpeg,image/jpg,image/png,application/pdf"
                  @change="handleIDFileSelect"
                />
                
                <label for="id-file-input" class="cursor-pointer">
                  <CloudArrowUpIcon class="w-10 h-10 mx-auto text-blue-500 mb-1.5" />
                  <p class="text-xs font-semibold text-gray-700 mb-1">
                    Upload your school ID
                  </p>
                  <p class="text-xs text-gray-500 mb-2">
                    JPG, PNG, PDF (Max {{ MAX_SIZE_MB }}MB)
                  </p>
                  <div class="inline-block px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors font-medium">
                    Choose File
                  </div>
                </label>
              </div>

              <!-- Error Message for ID -->
              <div v-if="idError" class="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <svg class="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
                <p class="text-xs text-red-700">{{ idError }}</p>
              </div>
            </div>

            <!-- ID Preview -->
            <div v-else class="relative">
              <!-- Image Preview -->
              <div v-if="idPreviewUrl" class="border-2 border-green-300 rounded-lg overflow-hidden">
                <img
                  :src="idPreviewUrl"
                  alt="ID Preview"
                  class="w-full h-auto max-h-40 object-contain bg-gray-50"
                />
              </div>
              
              <!-- PDF Preview -->
              <div v-else class="p-3 bg-gray-50 border-2 border-green-300 rounded-lg">
                <div class="flex items-center gap-2">
                  <DocumentTextIcon class="w-8 h-8 text-red-500 flex-shrink-0" />
                  <div class="flex-1 min-w-0">
                    <p class="font-semibold text-gray-900 text-xs">PDF Document</p>
                    <p class="text-xs text-gray-500 truncate">{{ idFile.name }}</p>
                  </div>
                </div>
              </div>
              
              <button
                @click="removeIDFile"
                :disabled="isUploading"
                class="absolute top-1.5 right-1.5 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg border-2 border-white disabled:opacity-50 disabled:cursor-not-allowed"
                title="Remove file"
              >
                <XMarkIcon class="w-3.5 h-3.5" />
              </button>
              <p class="text-xs text-gray-600 mt-1.5 truncate">🆔 {{ idFile.name }} ({{ formatFileSize(idFile.size) }})</p>
            </div>
          </div>

          <!-- Upload Info -->
          <div class="p-2.5 bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg">
            <p class="text-xs text-yellow-900 font-medium mb-1">⚠️ Important:</p>
            <ul class="text-xs text-yellow-800 space-y-0.5 ml-4 list-disc">
              <li>Both documents are required</li>
              <li>ID must show your current grade level</li>
              <li>Images should be clear and readable</li>
            </ul>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-2 px-4 pb-3 bg-white  pt-3 flex-shrink-0">
          <button
            @click="handleClose"
            :disabled="isUploading"
            class="px-4 py-2 text-sm text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            @click="handleUpload"
            :disabled="!canUpload"
            class="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium shadow-md flex items-center gap-2"
          >
            <CloudArrowUpIcon v-if="!isUploading" class="w-4 h-4" />
            <svg v-else class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ isUploading ? 'Uploading...' : 'Submit' }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active > div,
.modal-leave-active > div {
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-enter-from > div,
.modal-leave-to > div {
  transform: scale(0.9) translateY(-20px);
}
</style>

